import { Inject, Injectable } from '@angular/core';
import { LocaleUtil } from '@ddt-web/common';
import { APPLICATION_NAME_TOKEN, PortfolioRetrievalParamsConfig } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { PortfolioRetrievalParams } from '@ddt-web/domain';
import { LocalStorageKey } from '@ddt-web/generic-utils';
import { UrlUtils } from '@ddt-web/url';
import { UserDataService, UserProfile } from '@ddt-web/user-data';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { DataServiceApiConstants } from './constants/data-service-api.constants';
import { WweFeature } from './enums/wwe-feature.enum';

/**
 * The WweRcResolverService should always be used when deciding which rc to pass to a WWE API. It builds the rc based on the DataServiceApiConstants.WWE_RCS constants,
 * but allows us to provide rc overrides based on the user profile.
 * 
 * If an override is provided, a new directory should be created in TeamSite for the corresponding rc overrides.
 * e.g. if there is an override for Swiss users that points to `ch`, pc:security-details will become pc:ch:security-details
 */
@Injectable({ providedIn: 'root' })
export class WweRcResolverService {
	@InjectConfig(SharedConfigKey.WWE_RC_TENANT_OVERRIDES) private readonly tenantOverrides: { [userType: string]: string } = {};

	constructor(@Inject(APPLICATION_NAME_TOKEN) private appName: string, private userDataService: UserDataService) { }

	public getRcForUserType(rcType: string): Observable<string> {
		const mockCountry = localStorage.getItem(LocalStorageKey.MockCountry);
		const rc: string = DataServiceApiConstants.WWE_RCS[this.appName][rcType];

		return this.userDataService.getUserProfile().pipe(
			take(1),
			map((userProfile: UserProfile) => {
				let userType: string = userProfile?.internalUser
					? 'internal'
					: (userProfile?.userExtendedAttributes?.crmPrimaryCountryCode ?? userProfile?.userExtendedAttributes?.PRIMARY_COUNTRY_CODE)?.toLowerCase();
				if (!UrlUtils.isProdDmz() && userType === 'internal' && Boolean(mockCountry)) {
					userType = LocaleUtil.getCountryStringFromLocaleExpr(mockCountry);
				}
				if (!!this.tenantOverrides[userType]) {
					const rcParts: string[] = rc.split(':');
					rcParts.splice(rcParts.length - 1, 0, this.tenantOverrides[userType]);
					return rcParts.join(':');
				}
				return rc;
			})
		);
	}

	public getPortfolioRetrievalParams(): Observable<PortfolioRetrievalParams> {
		if (PortfolioRetrievalParamsConfig.SKIP_SECURITY_ENRICHMENT_APPS.includes(this.appName)) {
			return this.getRcForUserType(DataServiceApiConstants.SECURITY_DETAILS).pipe(
				map((rc: string) => ({
					skipSecurityEnrichment: true,
					securityDetailsRc: rc
				}))
			);
		} else {
			console.warn('Backend api params are not configured. Using default. Please configure.');
			return of(PortfolioRetrievalParamsConfig.DEFAULT);
		}
	}

	public getRcForWweFeature(rc: string, feature?: WweFeature): string {
		if (feature === WweFeature.COMPARE) {
			return DataServiceApiConstants.SECURITY_DETAILS_COMPARE;
		} else {
			return rc;
		}
	}
}
